/**
 * The plan Claude asks the forge's owner to approve (Claude Code's
 * ExitPlanMode; ADR-023 A14). The turn waits here: "Approve plan" lets
 * Claude leave plan mode and build in the same turn; "Keep planning" sends
 * the plan back with what to change, and Claude revises it.
 */
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ClipboardList, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { ForgePendingPlan } from '../types';

/** Mirrors FORGE_ANSWER_MAX_CHARS (the route's bound on feedback). */
const FEEDBACK_MAX_CHARS = 2000;

export interface ForgePlanCardProps {
  plan: ForgePendingPlan;
  onApprove: () => Promise<void>;
  onRevise: (feedback: string) => Promise<void>;
}

export function ForgePlanCard({ plan, onApprove, onRevise }: ForgePlanCardProps) {
  const [revising, setRevising] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [sending, setSending] = useState<'approve' | 'revise' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = async (kind: 'approve' | 'revise') => {
    if (sending) return;
    setSending(kind);
    setError(null);
    try {
      if (kind === 'approve') await onApprove();
      else await onRevise(feedback.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your decision');
      setSending(null);
    }
  };

  return (
    <section
      aria-label="Claude's plan"
      data-testid="forge-plan"
      className="flex max-h-[60vh] flex-col border-t bg-primary/5 px-4 py-3"
    >
      <p className="mb-2 flex items-center gap-2 text-sm font-medium">
        <ClipboardList className="h-4 w-4 text-primary" />
        Claude Code has a plan. Approve it to start building.
      </p>
      <div className="prose prose-sm dark:prose-invert min-h-0 max-w-none flex-1 overflow-y-auto break-words rounded-md border bg-background px-3 py-2 text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{plan.plan}</ReactMarkdown>
      </div>
      {revising && (
        <Textarea
          autoFocus
          aria-label="What should change in the plan?"
          placeholder="What should change? Claude revises the plan and asks again."
          className="mt-2"
          rows={3}
          value={feedback}
          maxLength={FEEDBACK_MAX_CHARS}
          disabled={sending !== null}
          onChange={(e) => setFeedback(e.target.value)}
        />
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="mt-3 flex justify-end gap-2">
        {revising ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRevising(false)}
              disabled={sending !== null}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void decide('revise')}
              disabled={sending !== null || !feedback.trim()}
            >
              {sending === 'revise' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Send back
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRevising(true)}
            disabled={sending !== null}
          >
            Keep planning
          </Button>
        )}
        {!revising && (
          <Button size="sm" onClick={() => void decide('approve')} disabled={sending !== null}>
            {sending === 'approve' && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Approve plan
          </Button>
        )}
      </div>
    </section>
  );
}
